import { Box, Button, Card, CardActionArea, CardContent, Chip, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { useAuth } from '../auth/AuthProvider';

/** A tile of the old view: the reference's label and code, and the page it opens here. */
interface Tile {
  label: string;
  code: string;
  to: string;
}

/**
 * The tiles of "Manage Inventory" in the reference's order (walk of 2026-10-15). "Untagged
 * MEID/Device ID" shows no code in the reference; Manage Inventory (PC345) guards it here.
 */
export const MANAGE_INVENTORY_TILES: Tile[] = [
  { label: 'Add Units', code: 'PC1', to: '/inventory/units/new' },
  { label: 'Update Inventory', code: 'PC391', to: '/inventory/update' },
  { label: 'Change ESN/SIM Status', code: 'PC50', to: '/inventory/bulk/change-sim-status' },
  { label: 'Adjustments', code: 'PC47', to: '/inventory/adjustments' },
  { label: 'Dropship Orders', code: 'PC7', to: '/inventory/dropship-orders' },
  { label: 'Reporting', code: 'PC19', to: '/inventory/reporting' },
  { label: 'Inventory Dashboard', code: 'PC21', to: '/inventory' },
  {
    label: 'Equipment & Accessories Request',
    code: 'PC169',
    to: '/shipment/equipment-requests',
  },
  { label: 'Untagged MEID/Device ID', code: 'PC345', to: '/inventory/bulk/untag-device' },
];

/**
 * Manage Inventory (PC345), the reference's "old view": a landing page of tiles, with
 * "Switch to New View" back to the Inventory Dashboard. A tile shows when its code is granted.
 */
export function ManageInventoryPage() {
  const { can } = useAuth();
  const tiles = MANAGE_INVENTORY_TILES.filter((t) => can(t.code));
  return (
    <Box>
      <PageHeader
        title="Manage Inventory"
        code="PC345"
        actions={
          <Button component={Link} to="/inventory">
            Switch to New View
          </Button>
        }
      />
      <Box
        component="nav"
        aria-label="Manage Inventory"
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
        }}
      >
        {tiles.map((t) => (
          <Card key={t.label} variant="outlined">
            <CardActionArea component={Link} to={t.to} sx={{ height: '100%' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontWeight: 600, flexGrow: 1 }}>{t.label}</Typography>
                <Chip size="small" label={t.code} />
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Box>
  );
}
